import { CheckOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  getFrontVerificaCheckAndSendMail,
  useGetFrontVerificaCheckMail,
} from '@framework/api-client/api/inquiries-compile/verifica-controller/verifica-controller.ts';
import { RichTextAdvancedRender } from '@framework/engine/inputs/rich-text-advanced/render/RichTextAdvancedRender.tsx';
import { type IWidgetComponentProps } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { getWidgetProps } from '@framework/engine/schema/widgets/repository/inputs/common/util/getWidgetProps.ts';
import { ConfigFormItem } from '@framework/engine/ui/form/ConfigFormItem.tsx';
import { useWatchField } from '@framework/form/hooks/use-watch-field/useWatchField.tsx';
import { useController } from '@framework/form/hooks/useController.ts';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { Button, message, Space, Tag } from 'antd';
import { isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import styled, { useTheme } from 'styled-components';
import { useDebounceValue } from 'usehooks-ts';
import { useFillableSubmissionContext } from '../../FillableSubmissionContext.tsx';
import {
  EmailVerificationState,
  emailVerificationWidgetStateFieldName,
} from './createEmailVerificationWidgetFormSchema.ts';
import { type IEmailVerificationWidget } from './IEmailVerificationWidget.ts';

const EMAIL_CHECK_DEBOUNCE_INTERVAL = 350;
const EMAIL_CHECK_AFTER_SEND_INTERVAL = 10_000;

const EmailSchema = yup.string().email().required();

const EmailValidationTextMessage = styled.div`
  display: flex;
  margin-top: -40px;
  .has-help-text {
    margin-top: 0;
  }
`;

const VerifiedTag = styled(Tag)`
  margin-right: 0;
  line-height: 38px;
  font-size: ${({ theme }) => theme.fontSize}px;
  padding-right: 12px;
  padding-left: 12px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
`;

export interface IEmailVerificationWidgetComponentProps
  extends IWidgetComponentProps<IEmailVerificationWidget> {}

export function EmailVerificationWidgetComponent(
  props: IEmailVerificationWidgetComponentProps,
) {
  const { widget } = props;

  const theme = useTheme();
  const { t } = useTranslation();

  const options = getWidgetProps(props);

  const { field: verificationStateField } = useController(
    emailVerificationWidgetStateFieldName(options.name),
    // defaultValue: EmailVerificationState.NotVerified,
  );

  const state = verificationStateField.value as EmailVerificationState;

  const texts = widget.layout;

  const helpText =
    state === EmailVerificationState.NotVerified
      ? texts?.startHelpText
      : state === EmailVerificationState.Verifying
        ? texts?.endHelpText
        : undefined;

  const email = useWatchField({
    name: options.name,
  }) as string;

  // Controlla se l'email è stata verificata
  const [debouncedEmail] = useDebounceValue(
    email,
    EMAIL_CHECK_DEBOUNCE_INTERVAL,
  );
  const { submission } = useFillableSubmissionContext();

  const verifyEmailParams = useMemo(
    () => ({
      email: debouncedEmail,
      idRichiesta: submission?.id ?? '',
    }),
    [debouncedEmail, submission?.id],
  );

  const verifyEmailResult = useGetFrontVerificaCheckMail(verifyEmailParams, {
    query: {
      enabled:
        submission?.id != null &&
        !options.readonly &&
        !options.preview &&
        !isEmpty(debouncedEmail) &&
        EmailSchema.isValidSync(debouncedEmail),
      refetchInterval:
        state == EmailVerificationState.Verifying
          ? EMAIL_CHECK_AFTER_SEND_INTERVAL
          : false,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
      onSuccess: verified => {
        if (
          verified &&
          verificationStateField.value !== EmailVerificationState.Verified
        ) {
          verificationStateField.onChange(EmailVerificationState.Verified);
        } else if (
          !verified &&
          verificationStateField.value !== EmailVerificationState.NotVerified
        ) {
          verificationStateField.onChange(EmailVerificationState.NotVerified);
        }
      },
    },
  });

  // Invio Email di Verifica
  const sendVerificationEmailMutation = useMutation({
    mutationFn: getFrontVerificaCheckAndSendMail,
    mutationKey: 'sendVerificationEmail',
  });

  const sendVerificationEmail = useCallback(async () => {
    try {
      const isValid = await EmailSchema.isValid(email);
      if (!isValid) {
        message.error(
          t(
            'email-verification-widget.invalid-email',
            `L'email inserita non è valida`,
          ),
        );
        return;
      }

      await sendVerificationEmailMutation.mutateAsync({
        email,
        idRichiesta: submission?.id ?? '',
      });
      message.success(
        t(
          'email-verification-widget.check-mail-box-to-confirm',
          'Ti è stata inviata una email di verifica alla tua casella di posta. Controlla la tua posta elettronica per confermare la tua email.',
        ),
      );
      verificationStateField.onChange(EmailVerificationState.Verifying);
    } catch (error: any) {
      console.error(error);
      const errorMessage =
        error?.response?.data?.descrizione ??
        t(
          'email-verification-widget.error-in-sending-verification-mail',
          "Errore durante l'invio dell'email di verifica",
        );
      message.error(errorMessage);
    }
  }, [sendVerificationEmailMutation, email, verificationStateField]);

  return (
    <div>
      <ConfigFormItem
        name={options.name}
        label={<RichTextAdvancedRender inline text={options.label} />}
        help={<RichTextAdvancedRender inline text={helpText} />}
        layout={{ hideVisualLabel: options.hideLabel }}
        readonly={options.readonly}
        widget={props.widget}
      >
        <Space.Compact size="large" block>
          <TextInput
            name={options.name}
            placeholder={options.placeholder}
            value={options.value as any}
            tabIndex={options.preview ? -1 : undefined}
            readOnly={props.readonly}
            onChange={() => {
              verificationStateField.onChange(
                EmailVerificationState.NotVerified,
              );
            }}
          />
          {state === EmailVerificationState.Verified && (
            <VerifiedTag color={theme.colorSuccess}>
              <CheckOutlined />{' '}
              {t('email-verification-widget.verified', 'Verificata')}
            </VerifiedTag>
          )}
          {!options.readonly &&
            state === EmailVerificationState.NotVerified && (
              <Button
                type="primary"
                disabled={verifyEmailResult.isFetching}
                loading={sendVerificationEmailMutation.isLoading}
                onClick={() => {
                  sendVerificationEmail();
                }}
              >
                {texts?.startButtonText ??
                  t('email-verification-widget.verify-email', 'Verifica Email')}
              </Button>
            )}
          {!options.readonly && state === EmailVerificationState.Verifying && (
            <Button
              loading={verifyEmailResult.isFetching}
              onClick={() => {
                verifyEmailResult.refetch();
              }}
            >
              <ReloadOutlined />{' '}
              {texts?.endButtonText ??
                t(
                  'email-verification-widget.confirm-reception',
                  'Conferma Ricezione',
                )}
            </Button>
          )}
        </Space.Compact>
      </ConfigFormItem>
      {/* alternativa messaggio di aiuto */}
      {/* <EmailValidationTextMessage
        className={clx({
          'has-help-text': options?.helpText ?? undefined,
        })}
      >
          <RichTextAdvancedRender hasCustomFont text={texts?.startHelpText} />
      </EmailValidationTextMessage> */}
    </div>
  );
}
